import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  Send, 
  Users, 
  Filter,
  RefreshCw
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import SourcingRfpService from '../../services/sourcingRFPService';

// Sub-components
import RfpPublishingSettings from './RfpPublishingSettings';
import VendorResponseManagement from './VendorResponses';

const PublishVendorManagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'publishing');
  const [rfps, setRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState(id || searchParams.get('rfpId') || '');

  useEffect(() => {
    const loadRfps = async () => {
      try {
        const res = await SourcingRfpService.getRfps();
        const list = res.data?.items || res.items || (Array.isArray(res.data) ? res.data : []);
        setRfps(list);
      } catch (err) {
        console.error('Failed to load RFPs', err);
      }
    };
    loadRfps();
  }, []);

  useEffect(() => {
    if (id && id !== selectedRfpId) {
      setSelectedRfpId(id);
    }
  }, [id, selectedRfpId]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId, rfpId: selectedRfpId });
  };

  const handleRfpChange = (newId) => {
    setSelectedRfpId(newId);
    setSearchParams({ tab: activeTab, rfpId: newId });
    // If the child components rely on useParams, we might need to navigate
    if (newId) {
       navigate(`/sourcing/publish-vendor-mgmt/${newId}?tab=${activeTab}`);
    } else {
       navigate(`/sourcing/publish-vendor-mgmt?tab=${activeTab}`);
    }
  };

  const tabs = [
    { id: 'publishing', label: 'Publishing & Invitations', icon: <Send size={18} /> },
    { id: 'responses', label: 'Vendor Responses', icon: <Users size={18} /> },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Publish & Vendor Management"
        description="Unified workspace for vendor invitations and submission tracking."
      />

      <div className="page-container">
        {/* Unified RFP Selector */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3 text-gray-400">
            <Filter size={20} />
            <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">
              Active Sourcing Event:
            </span>
          </div>
          <select
            className="flex-1 bg-gray-50 border border-gray-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            value={selectedRfpId}
            onChange={(e) => handleRfpChange(e.target.value)}
          >
            <option value="">Select an RFP to manage...</option>
            {rfps.map((rfp) => (
              <option key={rfp._id} value={rfp._id}>
                {rfp.rfpNumber} - {rfp.title} ({rfp.status})
              </option>
            ))}
          </select>
          <button 
            onClick={() => window.location.reload()}
            className="p-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
            title="Refresh Data"
          >
            <RefreshCw size={18} />
          </button>
        </div>

        {/* Custom Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {tabs.map((tab) => (activeTab === tab.id ? (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className="flex items-center gap-2 px-6 py-3 border-b-2 border-blue-600 text-blue-600 font-semibold transition-all"
            >
              {tab.icon}
              {tab.label}
            </button>
          ) : (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className="flex items-center gap-2 px-6 py-3 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all font-medium"
            >
              {tab.icon}
              {tab.label}
            </button>
          )))}
        </div>

        {/* Tab Content */}
        <div className="tab-content-wrapper">
          {activeTab === 'publishing' && (
            <div className="animate-in fade-in duration-300">
              {/* Note: We will need to slightly adjust RfpPublishingSettings to work as a nested component */}
              <RfpPublishingSettings key={`pub-${selectedRfpId}`} isNested={true} rfpId={selectedRfpId} />
            </div>
          )}
          {activeTab === 'responses' && (
            <div className="animate-in fade-in duration-300">
              <VendorResponseManagement key={`res-${selectedRfpId}`} isNested={true} rfpId={selectedRfpId} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PublishVendorManagement;
